// src/pages/History.jsx
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
} from 'react';

const HISTORY_TABS = [
  { key: 'section1', label: '2021 ~ Present' },
  { key: 'section2', label: '2016 ~ 2020' },
  { key: 'section3', label: '2011 ~ 2015' },
  { key: 'section4', label: '2005 ~ 2010' },
];

const History = forwardRef((props, ref) => {
  const scrollRef = useRef(null);
  const sectionRefs = useRef({});
  const rafRef = useRef(null);

  // 버튼 클릭으로 발생한 smooth scroll 중에는 active 자동계산을 잠깐 막기 위한 ref
  const activeLockRef = useRef(false);
  const activeLockTimerRef = useRef(null);
  const lockedSectionRef = useRef(null);

  const [activeSection, setActiveSection] = useState('section1');
  const [progress, setProgress] = useState(0);

  const isHorizontalMode = useCallback(() => {
    return window.matchMedia('(max-width: 1024px)').matches;
  }, []);

  const setSectionRef = useCallback(
    (sectionKey) => (el) => {
      if (el) {
        sectionRefs.current[sectionKey] = el;
      } else {
        delete sectionRefs.current[sectionKey];
      }
    },
    []
  );

  const getScrollInfo = useCallback(
    (scrollEl) => {
      const horizontal = isHorizontalMode();

      const currentScroll = horizontal
        ? scrollEl.scrollLeft
        : scrollEl.scrollTop;

      const maxScroll = horizontal
        ? scrollEl.scrollWidth - scrollEl.clientWidth
        : scrollEl.scrollHeight - scrollEl.clientHeight;

      return {
        horizontal,
        currentScroll,
        maxScroll: Math.max(0, maxScroll),
      };
    },
    [isHorizontalMode]
  );

  const getSectionOffset = useCallback((sectionKey, horizontal, scrollEl) => {
    const target = sectionRefs.current[sectionKey];
    if (!target) return 0;

    const scrollTarget = target.closest('dl') || target;
    const containerRect = scrollEl.getBoundingClientRect();
    const targetRect = scrollTarget.getBoundingClientRect();

    return horizontal
      ? scrollEl.scrollLeft + targetRect.left - containerRect.left
      : scrollEl.scrollTop + targetRect.top - containerRect.top;
  }, []);

  const getNextActiveSection = useCallback(
    (scrollEl, horizontal, currentScroll, maxScroll) => {
      const sectionOffsets = HISTORY_TABS.map((tab) => ({
        key: tab.key,
        offset: getSectionOffset(tab.key, horizontal, scrollEl),
      }));

      if (sectionOffsets.length === 0) {
        return 'section1';
      }

      /*
        마지막 영역 보정.
        2005는 가장 아래/오른쪽이라 scrollTo를 해도
        기준선까지 못 올라오는 경우가 있음.
        그래서 스크롤 끝에 가까우면 강제로 마지막 탭 active.
      */
      if (maxScroll > 0 && currentScroll >= maxScroll - 4) {
        return sectionOffsets[sectionOffsets.length - 1].key;
      }

      /*
        기존 getBoundingClientRect 기준보다 안정적인 방식.
        현재 스크롤 위치 + 화면 내부 기준점으로 active 계산.
      */
      const activePoint =
        currentScroll +
        (horizontal ? scrollEl.clientWidth * 0.42 : scrollEl.clientHeight * 0.36);

      let nextActive = sectionOffsets[0].key;

      sectionOffsets.forEach((section) => {
        if (section.offset <= activePoint) {
          nextActive = section.key;
        }
      });

      return nextActive;
    },
    [getSectionOffset]
  );

  const syncHistoryState = useCallback(
    ({ syncActive = true } = {}) => {
      const scrollEl = scrollRef.current;
      if (!scrollEl) return;

      const { horizontal, currentScroll, maxScroll } = getScrollInfo(scrollEl);

      const nextProgress =
        maxScroll <= 0 ? 0 : (currentScroll / maxScroll) * 100;

      setProgress(Math.min(100, Math.max(0, nextProgress)));

      /*
        버튼 클릭으로 smooth scroll 중일 때는 progress만 움직이고,
        history_item active는 클릭한 버튼 상태를 유지.
      */
      if (!syncActive || activeLockRef.current) {
        return;
      }

      const nextActive = getNextActiveSection(
        scrollEl,
        horizontal,
        currentScroll,
        maxScroll
      );

      setActiveSection((prev) => (prev === nextActive ? prev : nextActive));
    },
    [getScrollInfo, getNextActiveSection]
  );

  const releaseActiveLockSoon = useCallback(() => {
    if (activeLockTimerRef.current) {
      window.clearTimeout(activeLockTimerRef.current);
    }

    activeLockTimerRef.current = window.setTimeout(() => {
      activeLockRef.current = false;

      /*
        smooth scroll 종료 후에도 클릭한 탭이 유지되도록 한 번 더 보정.
        특히 2005~2010 마지막 구간에서 중요.
      */
      if (lockedSectionRef.current) {
        setActiveSection(lockedSectionRef.current);
        lockedSectionRef.current = null;
      }
    }, 180);
  }, []);

  const handleHistoryScroll = useCallback(() => {
    if (rafRef.current) return;

    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;

      syncHistoryState({
        syncActive: !activeLockRef.current,
      });

      if (activeLockRef.current) {
        releaseActiveLockSoon();
      }
    });
  }, [syncHistoryState, releaseActiveLockSoon]);

  const scrollToSection = useCallback(
    (sectionKey) => {
      const scrollEl = scrollRef.current;
      const target = sectionRefs.current[sectionKey];

      if (!scrollEl || !target) return;

      const { horizontal, maxScroll } = getScrollInfo(scrollEl);

      /*
        클릭 시 active는 즉시 고정.
        smooth scroll 중 onScroll이 active를 덮어쓰지 못하게 잠금.
      */
      activeLockRef.current = true;
      lockedSectionRef.current = sectionKey;
      setActiveSection(sectionKey);

      const rawOffset = getSectionOffset(sectionKey, horizontal, scrollEl);
      const gap = horizontal ? 16 : 24;

      /*
        마지막 구간은 offset 기준으로 이동하면 maxScroll에 막혀
        2011로 판정될 수 있으므로 아예 끝까지 보냄.
      */
      const isLastSection = sectionKey === HISTORY_TABS[HISTORY_TABS.length - 1].key;

      const nextPosition = isLastSection
        ? maxScroll
        : Math.min(maxScroll, Math.max(0, rawOffset - gap));

      if (horizontal) {
        scrollEl.scrollTo({
          left: nextPosition,
          behavior: 'smooth',
        });
      } else {
        scrollEl.scrollTo({
          top: nextPosition,
          behavior: 'smooth',
        });
      }

      releaseActiveLockSoon();
    },
    [getScrollInfo, getSectionOffset, releaseActiveLockSoon]
  );

  useEffect(() => {
    syncHistoryState();

    window.addEventListener('resize', handleHistoryScroll);

    return () => {
      window.removeEventListener('resize', handleHistoryScroll);

      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
      }

      if (activeLockTimerRef.current) {
        window.clearTimeout(activeLockTimerRef.current);
      }
    };
  }, [handleHistoryScroll, syncHistoryState]);

  const progressPosition = Math.min(96, Math.max(4, progress));

  return (
    <>
      <div className="partner_sub">
        <span className="partner_h2">
          <strong className="partner_strong">eKsys</strong>의 연혁 및 실적
        </span>
      </div>

      <div className="flex_div history" ref={ref}>
        <div className="history_con">
          <ul className="history_tab_list">
            {HISTORY_TABS.map((tab) => (
              <li key={tab.key}>               
                <button
                  type="button"
                  className={`history_item ${activeSection === tab.key ? 'active' : ''
                    }`}
                  onClick={() => scrollToSection(tab.key)}
                  aria-current={activeSection === tab.key ? 'true' : undefined}
                >
                  <span>{tab.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div
          className="history_scroll"
          ref={scrollRef}
          onScroll={handleHistoryScroll}
          style={{
            '--history-progress': `${progress}%`,
            '--history-dot': `${progressPosition}%`,
          }}
        >
          <div className="history_progress" aria-hidden="true">
            <span className="history_progress_fill" />
            <span className="history_progress_dot" />          
          </div>

          <div className="history_detail">
            <dl>
              <dt ref={setSectionRef('section1')}>2025</dt>
              <dd>
                <span>제주특별자치도 교통약자이동지원센터 관제 고도화 구축 개발 용역</span>
                <span>교통약자 이동지원 클라우드 SaaS 관제서비스 보안인증(CSAP) 갱신</span>
                <span>시스템 이동지원센터 관제 고도화 구축 개발 용역</span>
                <span>여주시 교통약자 관제시스템 AICC (AI 컨택센터, Artificial Intelligence Contact Center) 개발 용역</span>
                <span>교통약자 이동지원 관제서비스와 카카오T 연계 (충남교통약자)</span>
              </dd>
            </dl>
            <dl>
              <dt>2024</dt>
              <dd>
                <span>교통약자 클라우드 SaaS 관리 서비스 구축</span>
                <span>교통약자 K-PaaS 시험 인증 획득</span>
                <span>교통약자 클라우드 서비스 확인제 획득</span>
                <span>교통약자 이동지원 클라우드 SaaS 관제서비스 보안인증서(CSAP) 획득</span>
                <span>부산시 교통약자이동지원 관제시스템 고도화 작업</span>
                <span>경기도 광역이동지원센터 통합운영 고도화 작업</span>
              </dd>
            </dl>
            <dl>
              <dt>2023</dt>
              <dd>
                <span>국토부 교통약자 이동편의 정보관리시스템 ISP 사업 진행 완료</span>
                <span>공공부문 이용 SaaS 개발·검증(공공SaaS트랙) 과기부 과제 수행</span>
                <span>부산시 교통약자이동지원 관제시스템 구축</span>
                <span>경기도 광역이동지원센터 개선 용역 </span>
              </dd>
            </dl>
            <dl>
              <dt>2022</dt>
              <dd>
                <span>국토부 교통약자 이동편의 정보관리시스템 ISP 사업 수주</span>
                <span>연천군 광역이동지원 관제시스템 구축</span>
                <span>경기도 광역이동지원센터 전산시스템 연계·운영 고도화개발 완료</span>
                <span>여주시 광역이동지원 관제시스템 구축</span>
              </dd>
            </dl>
            <dl>
              <dt>2021</dt>
              <dd>
                <span>동두천시 광역이동지원 관제시스템 구축</span>
                <span>경기도 광역이동지원센터 전산시스템 연계·운영 고도화개발 수주</span>
                <span>인천광역시 이동지원 관제시스템 구축</span>
              </dd>
            </dl>
            <dl>
              <dt ref={setSectionRef('section2')}>2020</dt>
              <dd>
                <span>평택시 / 시흥시 교통약자이동지원 관제시스템 업그레이드 용역</span>
                <span>고양시 / 안산시 교통약자이동지원 홈페이지 및 모바일 App. 구축 용역</span>
                <span>제주도 교통약자이동지원 관제시스템 업그레이드 용역</span>
                <span>하남시 교통약자이동지원 관제시스템 구축</span>
                <span>가평군 교통약자이동지원 관제시스템 구축</span>
              </dd>
            </dl>
            <dl>
              <dt>2019</dt>
              <dd>
                <span>안성시 / 과천시 교통약자 이동지원 관제시스템 구축</span>
                <span>현대해상 보상포탈 시스템 Web GIS Engine 납품</span>
                <span>경기도 광역이동지원 교통약자 이동지원센터 및 경기도 내 수원시, 평택시, 부천시 등 16개 시/군 교통약자이동지원센터 유지보수</span>
                <span>경찰청 출동차량 길안내시스템 유지보수 (2019.5.9)</span>
              </dd>
            </dl>
            <dl>
              <dt>2018</dt>
              <dd>
                <span>경찰청 출동차량 길안내시스템 유지보수</span>
                <span>경찰청 여성청소년계 출동차량 길안내시스템 GIS 솔루션(SGMobile)공급</span>
                <span>경기도 광역이동지원 교통약자 이동지원센터 콜관제시스템 고도화 구축 (Open Platform 지원)</span>
                <span>부천시 교통약자 이동지원센터 관제시스템 구축</span>
                <span>부산광역시/대구광역시/대전광역시 소방안전본부 SG Mobile License 공급</span>
                <span>한국도로공사 고속도로 모니터링 시스템 구축</span>
              </dd>
            </dl>
            <dl>
              <dt>2017</dt>
              <dd>
                <span>평택 교통약자 이동지원 관제시스템(광역연계) 구축</span>
                <span>부산소방본부 출동차량 길안내 시스템 GIS 솔루션 (SGMobile) 공급</span>
                <span>SKT 네트웍 오앤에스 차량 관제 시스템 개발 계약</span>
                <span>충주시 교통약자 이동지원센터 관제시스템 구축</span>
                <span>경기도 광역이동지원 시스템 고도화 사업 구축 계약</span>
              </dd>
            </dl>
            
            <dl>
              <dt >2016</dt>
              <dd> 
                <span>포항시 교통약자 이동지원센터 관제시스템 구축</span>
                <span>한국도로공사 국도 교통정보 경로탐색 시스템 고도화 구축</span>
                <span>SKT 네트웍 오앤에스 차량 관제 시스템 개발 계약</span>
                <span>경찰청 출동차량 길안내시스템 GIS 엔진(SGMobile) 공급</span>
                <span>제주특별자치도 교통약자 이동지원 관제시스템 구축</span>
              </dd>
            </dl>
            <dl>
              <dt ref={setSectionRef('section3')}>2015</dt>
              <dd>
                <span>대구소방본부 모바일 소방활동시스템 구축을 위한 모바일 GIS 솔루션(SGMobile) 공급</span>
                <span>GS 인증_SmartInfo V1.0 (인증번호 : 15-0192) 한국도로공사 차세대CITS 센터시스템 GIS엔진 S/W 공급(SmartInfo V1.0) 경기도 광역이동지원 시스템 구축 계약</span>
                <span>한국도로공사 차세대 ITS (CITS) 구축사업 GIS Engine 소프트웨어 납품</span>
                <span>남양주시 교통약자 이동지원센터 콜관제시스템 구축사업</span>
                <span>오리온 영업활동관리 시스템 구축사업</span>
              </dd>
            </dl>
            <dl>
              <dt>2014</dt>
              <dd>
                <span>제주소방본부 모바일소방활동시스템 구축을 위한 GIS 솔루션(SG Mobile) 공급</span>
                <span>대전소방본부 모바일소방활동시스템 구축을 위한 GIS 솔루션(SG Mobile) 공급</span>
                <span>창원소방본부 모바일소방활동시스템 구축을 위한 GIS 솔루션(SG Mobile) 공급</span>
                <span>퀵/대리기사용 APP 연동 GIS 솔루션(SG Mobile) 공급</span>
                <span>한국도로공사 교통정보 통합 모니터링 S/W 개발 구축</span>
                <span>한국도로공사 콜센터 국도 교통정보 경로탐색 고도화 구축</span>
                <span>현대자동차 전주사업장 '수출완성차 위치관리 시스템' 계약 체결 (측지 및 GIS DB 구축/변환)</span>
              </dd>
            </dl>
            <dl>
              <dt>2013</dt>
              <dd>
                <span>한전 KDN '배전선로 설비진단 시스템 차량용 진단센서 구축' TalMap Mobile 공급 계약</span>
                <span>강원 소방본부 모바일 소방활동 시스템 APP 연동 SG Mobile 계약체결</span>
                <span>경남소방본부 '모바일 소방활동 시스템' 갤럭시탭 App 연동 SG Mobile 계약 체결</span>
                <span>엔알피시스템 화물관제시스템 TalMap Web 공급계약 체결</span>
                <span>SK브로드밴드 'LoISS 시스템' SmartMap Web 공급 계약 체결</span>
                <span>현대 HCN ‘SO GIS‘ 시스템 ‘SmartMap’ CS공급 계약 체결</span>
              </dd>
            </dl>
            <dl>
              <dt>2012</dt>
              <dd>
                <span>SK마케팅앤컴퍼니㈜ 2012년 영상기반 수집/편집툴 운영 계약 체결</span>
                <span>SK마케팅앤컴퍼니㈜ '스트리밍 맵 개발' 계약 체결</span>
                <span>KT ‘Olleh Map API Alliance’ 제휴 채결</span>
                <span>콜마너 스마트폰 SG Mobile 서비스런칭</span>
                <span>태국 iOS 단말 네비게이션 공급 계약 체결</span>
                <span>현대자동차 아산사업장 '수출완성차 위치관리 시스템' 계약 체결(측지 및 GIS DB 구축/변환)</span>
              </dd>
            </dl>

            <dl>
              <dt >2011</dt>
              <dd>
                <span>TalMap, SmartGEO, SmartTaxi GS 인증 획득</span>
                <span>안산시청 교통약자 이동지원 관제시스템 구축 사업 계약 체결</span>
                <span>고양시청 교통약자 이동지원 관제시스템 구축 사업 계약 체결</span>
                <span>SK C&C와 GCRM사업부문 GIS 전략적 협력 계약 체결</span>
                <span>SK C&C 아제르바이잔 바쿠 ITS 구축사업</span>
              </dd>
            </dl>
            <dl>
              <dt  ref={setSectionRef('section4')}>2010</dt>
              <dd>
                <span>수원시청 교통약자 이동지원 관제시스템 구축 사업 계약 체결</span>
                <span>시흥시청 교통약자 이동지원 관제시스템 구축 사업 계약 체결</span>
                <span>한국도로공사 콜센터상담원용 GIS시스템 구축 사업 계약 체결</span>
                <span>현대자동차 수출완성차 위치관리시스템 TalMap Web 공급 계약 체결(울산사업장 GIS DB 구축포함)</span>
                <span>경찰청 교통사고시스템 TalMap Web 공급 계약 체결</span>
              </dd>
            </dl>
            <dl>
              <dt>2009</dt>
              <dd>
                <span>한국교통연구원 TalMap SDK 공급</span>
                <span>현대해상화재보험㈜ 긴급출동 콜센터 시스템용 Web GIS 공급</span>
                <span>㈜KT 유/무선망 품질측정용 TalMap Mobile SDK 공급</span>
                <span>코리아로지스 화물관제 GIS 협력 계약 체결</span>
                <span>모바일엔트로피 도시가스 GIS 협력 계약 체결</span>
                <span>한마루IT 유선케이블 망 관리 GIS 협력 계약 체결</span>
                <span>지디에스케이 CRM사업 GIS 전략적 협력 계약 체결</span>
              </dd>
            </dl>
            <dl>
              <dt>2008</dt>
              <dd>
                <span>한모비 Hanmobi, 한맵 Hanmap 상표 출원</span>
                <span>KT파워텔 "택시 관제 사업" 독점 계약 체결</span>
                <span>현대해상 긴급출동 GIS부문 납품 계약 체결</span>
                <span>SG Global 상표 출원</span>
                <span>탈맵 TalMap 상표 출원</span>
                <span>스마트지이오 smartGEO, 스마트인포 SmartInfo, 스마트비즈 SmartBiz 상표 출원</span>
              </dd>
            </dl>
            <dl>
              <dt>2007</dt>
              <dd>
                <span>'KT 파워텔' 파트너 社지정</span>
                <span>서울시 노선편성시스템 BIS GIS 납품</span>
                <span>SmartMap Mobile SDK 출시</span>
                <span>㈜이루온 대리운전 배차 시스템 개발 납품</span>
                <span>㈜에코센스 - 폐기물 차량 관제 시스템 GIS부문 납품, 한지도 Hanjido 상표등록 완료</span>
                <span>기업부설연구소 인증</span>
                <span>정보통신부 위치정보사업 허가</span>
              </dd>
            </dl>
            <dl>
              <dt>2006</dt>
              <dd>
                <span>㈜이케이시스로 사명 변경 및 법인 전환</span>
                <span>한국고속철도 KTX 물류 추적 및 조회 솔루션 개발</span>
                <span>현대/기아 자동차 GIS시스템 납품</span>
                <span>교통정보 표출 시스템 프로그램 등록</span>
                <span>디지털 기상표출 시스템 프로그램 등록</span>
                <span>㈜종근당 영업관리 시스템 개발</span>
                <span>야후코리아 '실시간 교통상황 표출 시스템' GIS웹 엔진 납품</span>
                <span>'SmartMap' 상표등록 완료</span>
                <span>'SmartWorldMap' 출시</span>
                <span>스마트맵 플러그인 플랫폼 ‘한지도’ 출시</span>
                <span>보건복지부 GIS 시스템 구축</span>
                <span>벤처기업 지정</span>
                <span>부산/광주광역시 ‘BIS’ 시스템 GIS엔진 납품</span>
              </dd>
            </dl>

            <dl>
              <dt>2005</dt>
              <dd>
                <span>㈜현대자동차 '모젠' Smartmap™ Package Web용 제공 및 개발지원</span>
                <span>Smartmap™ Package Web용 제공 - 기상청 디지털 예보 사업 팀</span>
                <span>Smartmap™ Package Web용 제공 - 기상청 행정DB구축사업</span>
                <span>㈜아이엔윈 Smartmap™ Web Gis Engine 제공 및 Telematics ITS 시스템 구축</span>
                <span>신 개념 GIS Engine SmartmapGX3.0™ 출시</span>
                <span>서울시 버스 관제 솔루션 - GIS Engine제공</span>
                <span>한국감정평가협회 GIS Engine Smartmap™ 제공</span>
              </dd>
            </dl>

          </div>
        </div>
      </div>
    </>
  );
});

export default History;

// import React, { useRef, useState, forwardRef } from 'react';

// const History = forwardRef((props, ref) => {
  
//   const section1Ref = useRef(null);
//   const section2Ref = useRef(null);
//   const section3Ref = useRef(null);
//   const section4Ref = useRef(null);
//   const [activeSection, setActiveSection] = useState(null);
  
//   const scrollToSection = (sectionRef, sectionName) => {
//     if (sectionRef && sectionRef.current) {
//       sectionRef.current.scrollIntoView({ behavior: 'smooth', block: "nearest", inline: "end" });
//       setActiveSection(sectionName); // 현재 활성화된 섹션 상태 업데이트
//     }
//   };

  


//   return (
//     <>
//       <div className='partner_sub'>
//         <span className='partner_h2'><strong className='partner_strong'>eKsys</strong>의 연혁 및 실적</span>
//       </div>
//       <div className='flex_div history' ref={ref}>

//         <div className='history_con'>
//           <ul>
//             <li className={`history_item ${activeSection === 'section1' ? 'active' : ''}`} onClick={() => scrollToSection(section1Ref, 'section1')}><span>2021 ~ Present</span></li>
//             <li className={`history_item ${activeSection === 'section2' ? 'active' : ''}`} onClick={() => scrollToSection(section2Ref, 'section2')}><span>2016 ~ 2020</span></li>
//             <li className={`history_item ${activeSection === 'section3' ? 'active' : ''}`} onClick={() => scrollToSection(section3Ref, 'section3')}><span>2011 ~ 2015</span></li>
//             <li className={`history_item ${activeSection === 'section4' ? 'active' : ''}`} onClick={() => scrollToSection(section4Ref, 'section4')}><span>2005 ~ 2010</span></li>
//           </ul>
//         </div>
//         <div className='history_scroll'>
//         {/* <div className='scroller' style={{top: `${scrollerTop}px`}} /> */}
//           <div className='scroll_line' />
//           <div className='history_detail'>
//             <dl>
//               <dt ref={section1Ref}>2025</dt>
//               <dd>
//                 <span>제주특별자치도 교통약자이동지원센터 관제 고도화 구축 개발 용역</span>
//                 <span>교통약자 이동지원 클라우드 SaaS 관제서비스 보안인증(CSAP) 갱신</span>
//                 <span>시스템 이동지원센터 관제 고도화 구축 개발 용역</span>
//                 <span>여주시 교통약자 관제시스템 AICC (AI 컨택센터, Artificial Intelligence Contact Center) 개발 용역</span>
//                 <span>교통약자 이동지원 관제서비스와 카카오T 연계 (충남교통약자)</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2024</dt>
//               <dd>
//                 <span>교통약자 클라우드 SaaS 관리 서비스 구축</span>
//                 <span>교통약자 K-PaaS 시험 인증 획득</span>
//                 <span>교통약자 클라우드 서비스 확인제 획득</span>
//                 <span>교통약자 이동지원 클라우드 SaaS 관제서비스 보안인증서(CSAP) 획득</span>
//                 <span>부산시 교통약자이동지원 관제시스템 고도화 작업</span>
//                 <span>경기도 광역이동지원센터 통합운영 고도화 작업</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2023</dt>
//               <dd>
//                 <span>국토부 교통약자 이동편의 정보관리시스템 ISP 사업 진행 완료</span>
//                 {/* <span>교통약자 클라우드 SaaS 관리 서비스 구축</span> */}
//                 <span>공공부문 이용 SaaS 개발·검증(공공SaaS트랙) 과기부 과제 수행</span>
//                 <span>부산시 교통약자이동지원 관제시스템 구축</span>
//                 <span>경기도 광역이동지원센터 개선 용역 </span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2022</dt>
//               <dd>
//                 <span>국토부 교통약자 이동편의 정보관리시스템 ISP 사업 수주</span>
//                 <span>연천군 광역이동지원 관제시스템 구축</span>
//                 <span>경기도 광역이동지원센터 전산시스템 연계·운영 고도화개발 완료</span>
//                 <span>여주시 광역이동지원 관제시스템 구축</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2021</dt>
//               <dd>
//                 <span>동두천시 광역이동지원 관제시스템 구축</span>
//                 <span>경기도 광역이동지원센터 전산시스템 연계·운영 고도화개발 수주</span>
//                 <span>인천광역시 이동지원 관제시스템 구축</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2020</dt>
//               <dd>
//                 <span>평택시 / 시흥시 교통약자이동지원 관제시스템 업그레이드 용역</span>
//                 <span>고양시 / 안산시 교통약자이동지원 홈페이지 및 모바일 App. 구축 용역</span>
//                 <span>제주도 교통약자이동지원 관제시스템 업그레이드 용역</span>
//                 <span>하남시 교통약자이동지원 관제시스템 구축</span>
//                 <span>가평군 교통약자이동지원 관제시스템 구축</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2019</dt>
//               <dd>
//                 <span>안성시 / 과천시 교통약자 이동지원 관제시스템 구축</span>
//                 <span>현대해상 보상포탈 시스템 Web GIS Engine 납품</span>
//                 <span>경기도 광역이동지원 교통약자 이동지원센터 및 경기도 내 수원시, 평택시, 부천시 등 16개 시/군 교통약자이동지원센터 유지보수</span>
//                 <span>경찰청 출동차량 길안내시스템 유지보수 (2019.5.9)</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2018</dt>
//               <dd>
//                 <span>경찰청 출동차량 길안내시스템 유지보수</span>
//                 <span>경찰청 여성청소년계 출동차량 길안내시스템 GIS 솔루션(SGMobile)공급</span>
//                 <span>경기도 광역이동지원 교통약자 이동지원센터 콜관제시스템 고도화 구축 (Open Platform 지원)</span>
//                 <span>부천시 교통약자 이동지원센터 관제시스템 구축</span>
//                 <span>부산광역시/대구광역시/대전광역시 소방안전본부 SG Mobile License 공급</span>
//                 <span>한국도로공사 고속도로 모니터링 시스템 구축</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2017</dt>
//               <dd>
//                 <span>평택 교통약자 이동지원 관제시스템(광역연계) 구축</span>
//                 <span>부산소방본부 출동차량 길안내 시스템 GIS 솔루션 (SGMobile) 공급</span>
//                 <span>SKT 네트웍 오앤에스 차량 관제 시스템 개발 계약</span>
//                 <span>충주시 교통약자 이동지원센터 관제시스템 구축</span>
//                 <span>경기도 광역이동지원 시스템 고도화 사업 구축 계약</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2016</dt>
//               <dd ref={section2Ref}> 
//                 <span>포항시 교통약자 이동지원센터 관제시스템 구축</span>
//                 <span>한국도로공사 국도 교통정보 경로탐색 시스템 고도화 구축</span>
//                 <span>SKT 네트웍 오앤에스 차량 관제 시스템 개발 계약</span>
//                 <span>경찰청 출동차량 길안내시스템 GIS 엔진(SGMobile) 공급</span>
//                 <span>제주특별자치도 교통약자 이동지원 관제시스템 구축</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2015</dt>
//               <dd>
//                 <span>대구소방본부 모바일 소방활동시스템 구축을 위한 모바일 GIS 솔루션(SGMobile) 공급</span>
//                 <span>GS 인증_SmartInfo V1.0 (인증번호 : 15-0192) 한국도로공사 차세대CITS 센터시스템 GIS엔진 S/W 공급(SmartInfo V1.0) 경기도 광역이동지원 시스템 구축 계약</span>
//                 <span>한국도로공사 차세대 ITS (CITS) 구축사업 GIS Engine 소프트웨어 납품</span>
//                 <span>남양주시 교통약자 이동지원센터 콜관제시스템 구축사업</span>
//                 <span>오리온 영업활동관리 시스템 구축사업</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2014</dt>
//               <dd>
//                 <span>제주소방본부 모바일소방활동시스템 구축을 위한 GIS 솔루션(SG Mobile) 공급</span>
//                 <span>대전소방본부 모바일소방활동시스템 구축을 위한 GIS 솔루션(SG Mobile) 공급</span>
//                 <span>창원소방본부 모바일소방활동시스템 구축을 위한 GIS 솔루션(SG Mobile) 공급</span>
//                 <span>퀵/대리기사용 APP 연동 GIS 솔루션(SG Mobile) 공급</span>
//                 <span>한국도로공사 교통정보 통합 모니터링 S/W 개발 구축</span>
//                 <span>한국도로공사 콜센터 국도 교통정보 경로탐색 고도화 구축</span>
//                 <span>현대자동차 전주사업장 '수출완성차 위치관리 시스템' 계약 체결 (측지 및 GIS DB 구축/변환)</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2013</dt>
//               <dd>
//                 <span>한전 KDN '배전선로 설비진단 시스템 차량용 진단센서 구축' TalMap Mobile 공급 계약</span>
//                 <span>강원 소방본부 모바일 소방활동 시스템 APP 연동 SG Mobile 계약체결</span>
//                 <span>경남소방본부 '모바일 소방활동 시스템' 갤럭시탭 App 연동 SG Mobile 계약 체결</span>
//                 <span>엔알피시스템 화물관제시스템 TalMap Web 공급계약 체결</span>
//                 <span>SK브로드밴드 'LoISS 시스템' SmartMap Web 공급 계약 체결</span>
//                 <span>현대 HCN ‘SO GIS‘ 시스템 ‘SmartMap’ CS공급 계약 체결</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2012</dt>
//               <dd>
//                 <span>SK마케팅앤컴퍼니㈜ 2012년 영상기반 수집/편집툴 운영 계약 체결</span>
//                 <span>SK마케팅앤컴퍼니㈜ '스트리밍 맵 개발' 계약 체결</span>
//                 <span>KT ‘Olleh Map API Alliance’ 제휴 채결</span>
//                 <span>콜마너 스마트폰 SG Mobile 서비스런칭</span>
//                 <span>태국 iOS 단말 네비게이션 공급 계약 체결</span>
//                 <span>현대자동차 아산사업장 '수출완성차 위치관리 시스템' 계약 체결(측지 및 GIS DB 구축/변환)</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2011</dt>
//               <dd ref={section3Ref}>
//                 <span>TalMap, SmartGEO, SmartTaxi GS 인증 획득</span>
//                 <span>안산시청 교통약자 이동지원 관제시스템 구축 사업 계약 체결</span>
//                 <span>고양시청 교통약자 이동지원 관제시스템 구축 사업 계약 체결</span>
//                 <span>SK C&C와 GCRM사업부문 GIS 전략적 협력 계약 체결</span>
//                 <span>SK C&C 아제르바이잔 바쿠 ITS 구축사업</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2010</dt>
//               <dd>
//                 <span>수원시청 교통약자 이동지원 관제시스템 구축 사업 계약 체결</span>
//                 <span>시흥시청 교통약자 이동지원 관제시스템 구축 사업 계약 체결</span>
//                 <span>한국도로공사 콜센터상담원용 GIS시스템 구축 사업 계약 체결</span>
//                 <span>현대자동차 수출완성차 위치관리시스템 TalMap Web 공급 계약 체결(울산사업장 GIS DB 구축포함)</span>
//                 <span>경찰청 교통사고시스템 TalMap Web 공급 계약 체결</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2009</dt>
//               <dd>
//                 <span>한국교통연구원 TalMap SDK 공급</span>
//                 <span>현대해상화재보험㈜ 긴급출동 콜센터 시스템용 Web GIS 공급</span>
//                 <span>㈜KT 유/무선망 품질측정용 TalMap Mobile SDK 공급</span>
//                 <span>코리아로지스 화물관제 GIS 협력 계약 체결</span>
//                 <span>모바일엔트로피 도시가스 GIS 협력 계약 체결</span>
//                 <span>한마루IT 유선케이블 망 관리 GIS 협력 계약 체결</span>
//                 <span>지디에스케이 CRM사업 GIS 전략적 협력 계약 체결</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2008</dt>
//               <dd>
//                 <span>한모비 Hanmobi, 한맵 Hanmap 상표 출원</span>
//                 <span>KT파워텔 "택시 관제 사업" 독점 계약 체결</span>
//                 <span>현대해상 긴급출동 GIS부문 납품 계약 체결</span>
//                 <span>SG Global 상표 출원</span>
//                 <span>탈맵 TalMap 상표 출원</span>
//                 <span>스마트지이오 smartGEO, 스마트인포 SmartInfo, 스마트비즈 SmartBiz 상표 출원</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2007</dt>
//               <dd>
//                 <span>'KT 파워텔' 파트너 社지정</span>
//                 <span>서울시 노선편성시스템 BIS GIS 납품</span>
//                 <span>SmartMap Mobile SDK 출시</span>
//                 <span>㈜이루온 대리운전 배차 시스템 개발 납품</span>
//                 <span>㈜에코센스 - 폐기물 차량 관제 시스템 GIS부문 납품, 한지도 Hanjido 상표등록 완료</span>
//                 <span>기업부설연구소 인증</span>
//                 <span>정보통신부 위치정보사업 허가</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt>2006</dt>
//               <dd>
//                 <span>㈜이케이시스로 사명 변경 및 법인 전환</span>
//                 <span>한국고속철도 KTX 물류 추적 및 조회 솔루션 개발</span>
//                 <span>현대/기아 자동차 GIS시스템 납품</span>
//                 <span>교통정보 표출 시스템 프로그램 등록</span>
//                 <span>디지털 기상표출 시스템 프로그램 등록</span>
//                 <span>㈜종근당 영업관리 시스템 개발</span>
//                 <span>야후코리아 '실시간 교통상황 표출 시스템' GIS웹 엔진 납품</span>
//                 <span>'SmartMap' 상표등록 완료</span>
//                 <span>'SmartWorldMap' 출시</span>
//                 <span>스마트맵 플러그인 플랫폼 ‘한지도’ 출시</span>
//                 <span>보건복지부 GIS 시스템 구축</span>
//                 <span>벤처기업 지정</span>
//                 <span>부산/광주광역시 ‘BIS’ 시스템 GIS엔진 납품</span>
//               </dd>
//             </dl>
//             <dl>
//               <dt >2005</dt>
//               <dd ref={section4Ref}>
//                 <span>㈜현대자동차 '모젠' Smartmap™ Package Web용 제공 및 개발지원</span>
//                 <span>Smartmap™ Package Web용 제공 - 기상청 디지털 예보 사업 팀</span>
//                 <span>Smartmap™ Package Web용 제공 - 기상청 행정DB구축사업</span>
//                 <span>㈜아이엔윈 Smartmap™ Web Gis Engine 제공 및 Telematics ITS 시스템 구축</span>
//                 <span>신 개념 GIS Engine SmartmapGX3.0™ 출시</span>
//                 <span>서울시 버스 관제 솔루션 - GIS Engine제공</span>
//                 <span>한국감정평가협회 GIS Engine Smartmap™ 제공</span>
//               </dd>
//             </dl>
//           </div>
//         </div>
//       </div>
//     </>
//   )
// })

// export default History
